import { Rating, WrongPartTag } from './types';

// ─── Form copy ───────────────────────────────────────────────────────────────
// Verbatim from Requirements 1, 2 and 15. Changing any of these changes the requirement.

export const FORM_HEADER = 'Merlin · feedback';
export const FORM_TITLE = 'Was our analysis useful?';

export const FIELD_1_LABEL = 'Was this useful?';
export const FIELD_2_LABEL = 'What should it have said or done?';
export const FIELD_2_HELPER_TEXT =
  'Works for any answer — a root cause, a correct reply, or where it should have gone.';
export const FIELD_3_LABEL = 'What did we get wrong?';
export const FIELD_4_LABEL = 'Which part was wrong?';
/**
 * Field 2's label for a positive rating.
 *
 * "What should it have said or done?" presupposes the answer was wrong, so on a Yes it reads as an
 * accusation of a reply the user just endorsed. A positive rating has no diagnostic fields at all, which
 * leaves this the only input — so it asks for a comment rather than a correction.
 */
export const FIELD_2_LABEL_POSITIVE = 'Anything else you want to add?';
export const FIELD_2_HELPER_TEXT_POSITIVE =
  'Optional. Submit or skip without it.';

/** Rendered order is part of the requirement, so these are arrays rather than sets. */
export const RATING_OPTIONS = ['Up', 'Down'] as const;
export const WRONG_PART_TAG_OPTIONS: readonly WrongPartTag[] = [
  'Addressed the other problem',
  'Missing information',
  'Incorrect information',
];

export const FREE_TEXT_STORAGE_NOTICE =
  'Free-text entries are stored for 90 days and can be read by the Search Services team.';

/**
 * The chat rating controls.
 *
 * A prompt precedes the two buttons because "Useful / Not useful" sitting bare under a response reads
 * as an unexplained pair of controls rather than a question. "Helpful" over "Useful" only because it is
 * the more common phrasing for this affordance, so it needs less reading.
 *
 * There is deliberately no "rating unavailable" notice. A response whose memory event never landed now
 * renders no controls at all, so there is no refused click left to explain.
 */
/**
 * Shown on the standalone ticket page after Skip, in place of the form.
 *
 * States that nothing was stored, because the reader cannot otherwise tell a skip from a submission
 * that silently failed — and on this surface there is no modal closing to signal the difference.
 */
/**
 * Validation messages for the two fields a `No` rating requires.
 *
 * A bare `Down` is the least actionable thing the form can collect: it says something was wrong without
 * saying what, which cannot be triaged or acted on. Requiring both is what makes the negative path worth
 * reading, and it is the only path that requires anything — `Up` asks for nothing.
 */
export const FIELD_3_REQUIRED_MESSAGE =
  'Tell us what we got wrong so this can be acted on.';
export const FIELD_4_REQUIRED_MESSAGE =
  'Select at least one part that was wrong.';

export const PAGE_SKIPPED_MESSAGE =
  'No feedback was recorded. You can close this tab, or reopen the link from the ticket if you change your mind.';

export const RATING_PROMPT = 'Was this helpful?';

/**
 * Self-describing rather than answers to the prompt.
 *
 * These were `Yes` and `No`, which read as ambiguous beside a thumbs-down: "No" could as easily mean
 * "no, it was not helpful" as "no, I do not want to rate this". The label now says what the button does on
 * its own, so it survives being read without the prompt — which is how a screen reader encounters it, and
 * how anyone scanning the transcript does.
 *
 * Kept as text beside the icon rather than going icon-only: the icon carries the polarity at a glance, and
 * the text is what remains when the icon does not render.
 */
export const RATING_UP_LABEL = 'Helpful';
export const RATING_DOWN_LABEL = 'Not helpful';

/**
 * The one place a wire value becomes something a person reads.
 *
 * `Up`/`Down` exist to match what Insight writes to the shared `Vote` column; they are a poor answer to
 * "Was this helpful?", which is the question actually on screen. Every surface renders through this map, so
 * the chat buttons and the form's field 1 cannot drift into naming the same value two different ways.
 *
 * Typed `Record<Rating, string>` rather than a lookup with a fallback: a new Rating should fail to compile
 * here rather than reach a user as a raw `Up`.
 */
export const RATING_LABELS: Readonly<Record<Rating, string>> = {
  Up: RATING_UP_LABEL,
  Down: RATING_DOWN_LABEL,
};

/**
 * Shown in place of the prompt once a rating is stored, and it **names the rating**.
 *
 * The recorded state was previously signalled only by the active button switching to the `primary`
 * variant — a colour change on one of two adjacent buttons, easy to miss and hard to tell from hover. A
 * rating is a write to a durable store, so it earns an explicit acknowledgement.
 *
 * It names which way was rated because the buttons are borderless (`variant="link"`), and a borderless
 * button has no selected state to carry that. Saying only "Feedback recorded" would leave the reader
 * unable to tell what was recorded without re-reading their own click — so the information moved here
 * rather than being dropped along with the button chrome.
 *
 * Replaces the prompt rather than sitting alongside it: once you have answered, "Was this helpful?" is
 * stale. The buttons stay operable, because the stable sort key makes a re-rating an in-place replacement
 * — so changing your mind is supported and must not be hidden behind the confirmation.
 */
export function ratingRecordedNotice(rating: Rating): string {
  return `Feedback recorded: ${RATING_LABELS[rating]}`;
}

/**
 * Offered **after** a rating is stored, not before it.
 *
 * A thumbs-up is a one-click gesture, and putting a form in front of it trades most of the ratings for a
 * few comments — the reason the positive control submits immediately is to capture the cheap signal at all.
 * But the option to elaborate should exist, and here it is nearly free: the sort key is a total function of
 * kind, target and submitter with no timestamp, so submitting again with a comment **replaces the same
 * item**. Recording first and enriching later produces one record, not two.
 *
 * Deliberately not called "Edit": the rating is already saved, and this adds to it rather than putting it
 * back into a draft state.
 */
export const RATING_ADD_DETAILS_LABEL = 'Add details';

// ─── Bounds ──────────────────────────────────────────────────────────────────

export const FREE_TEXT_MAX_LENGTH = 2000;
export const MESSAGE_REF_MAX_LENGTH = 512;
export const SUBMITTER_MAX_LENGTH = 64;
export const SORT_KEY_MAX_BYTES = 1024;
export const FREE_TEXT_RETENTION_DAYS = 90;

// ─── Timeouts and retries ────────────────────────────────────────────────────

export const SUBMIT_TIMEOUT_MS = 10_000;

/**
 * Deadline on the ticket page's context read, and therefore on how long the page withholds the
 * form (Requirement 10 criteria 5 and 7).
 *
 * Raised from 5s deliberately. Because the form waits on this read, a deadline below the read's
 * tail latency does not protect the user — it disables the existence check, since a timeout is
 * classified as *unknown* and renders the form. Do not lower this to improve perceived latency:
 * that trades a real integrity check for a cosmetic gain.
 */
export const CONTEXT_TIMEOUT_MS = 60_000;

/**
 * Bounds on the `create-event` retry of Requirement 6 criterion 10. These bound the *attempt*, not
 * the wait for an identifier — there is no deadline after which an unresolved identifier is treated
 * as resolved, because there is nothing to fall back to.
 *
 * There is deliberately no `MEMORY_REF_RESOLUTION_MS`. The controls render throughout and simply
 * stay non-operable until the identifier arrives.
 */
export const CREATE_EVENT_MAX_ATTEMPTS = 3;
export const CREATE_EVENT_RETRY_BASE_MS = 300;

// ─── Classification ──────────────────────────────────────────────────────────

/**
 * There is deliberately **no `TAG_TO_BUCKET` here.** The table lives once, in
 * `server/utils/merlinRouting.ts`.
 *
 * The client never classifies: it submits the selected tags and reads the assigned `reviewBuckets`
 * back off the mutation response (Requirement 12 criteria 4 and 5 put classification in the
 * Routing_Engine). So a client-side copy would have no consumer — and because `server/` and `src/`
 * are separate build units that cannot import from one another, the two copies could never be
 * unified, only kept in sync by hand.
 *
 * It would also invite the one inference Requirement 12 criteria 1 to 3 forbid. The mapping is
 * one-way: an assigned bucket permits no conclusion about which tag produced it, because a later
 * revision may map two tags onto one bucket. A client holding the table is a client that can invert
 * it.
 *
 * The form still needs `WRONG_PART_TAG_OPTIONS` above to render field 4, and `ReviewBucket` in
 * `types.ts` to type the mutation response. Neither requires the mapping.
 */

// ─── Deep link ───────────────────────────────────────────────────────────────

export const DEEP_LINK_VERSION = '1';

/**
 * Held as data so retiring a version is a one-line change and no parser is deleted.
 *
 * 18 months because the period has to exceed the working lifetime of a ticket comment carrying a
 * link: SIM-T tickets are reopened during recurrence investigations and year-over-year comparisons,
 * so a shorter period would expire links still reachable from live tickets.
 */
export const DEEP_LINK_COMPATIBILITY_MONTHS = 18;

/**
 * The literal AgentCore actor a ticket analysis is written under.
 *
 * `ListEvents` is scoped by `(memoryId, actorId, sessionId)`, so reading a ticket session under any
 * other actor returns an empty list from a *successful* call — indistinguishable from a genuinely
 * unanalysed ticket, and it would report `analysis-not-found` for every ticket in the fleet while
 * every metric reported success. Never derive this from the authenticated identity.
 */
export const TICKET_ACTOR_ID = 'ticket_interceptor';

/**
 * There is deliberately no `SESSION_ID_MIN_LENGTH` or `SESSION_ID_PAD_CHAR` here any more.
 *
 * They existed to mirror the interceptor's padding so the page could recompute a session name. That mirror
 * is gone — see the note in `deepLink.ts` — because the interceptor interpolates whichever ticket
 * identifier it was given, and on a real event that is the extended UUID form rather than the short id in
 * the link's path. Keeping the constants would invite the derivation back.
 */

// ─── Feedback_Page copy ──────────────────────────────────────────────────────
/**
 * One message per non-renderable outcome of Requirement 8.
 *
 * The two `sessionId` causes — `sessionId-pattern` and `sessionId-mismatch` — deliberately share the
 * invalid-link message with the format causes. The distinction between them exists for the metric
 * dimension, not for the reader: a user handed a bad link can do nothing with the knowledge that our
 * derivation drifted, and the remedy is the same either way.
 */
export const PAGE_INVALID_LINK_MESSAGE =
  'This feedback link is not valid. Please use a link from a recent Merlin ticket analysis.';

export const PAGE_EXPIRED_LINK_MESSAGE =
  'This feedback link has expired. Open the most recent Merlin analysis comment on the ticket to get a current link.';

/**
 * Names **both** things an empty read can mean, because it cannot distinguish them.
 *
 * The read succeeded and returned zero events, so this is not a failure and offering a retry would be
 * wrong. But it is also not proof that no analysis ever ran: AgentCore Memory is short-term, and an
 * analysis whose events are no longer retained reads exactly like one that never happened. The earlier
 * wording — "No Merlin analysis exists for this ticket" — asserted the first and was simply false for the
 * second, telling a user their analysis never existed while it sat in the comment above the link.
 *
 * So the wording covers the disjunction rather than picking a side, and closes by stating the consequence
 * — feedback is not available — rather than lecturing the reader that there is nothing to give feedback
 * on. Feedback is still declined, which is the point of the gate: with no event there is nothing to
 * attribute a rating to.
 */
export const PAGE_ANALYSIS_NOT_FOUND_MESSAGE =
  'We could not find the Merlin analysis for this ticket. Either it was never analysed, or its analysis history is no longer retained — feedback is not available.';

/**
 * The read failed or timed out, so whether an analysis exists is unknown.
 *
 * Separate from PAGE_ANALYSIS_NOT_FOUND_MESSAGE because that message asserts an absence, and asserting one
 * on a failed read is a lie the user can act on — they would stop trying. Both messages decline the form,
 * for the same reason: with no event id there is nothing to attribute a rating to. Only this one is
 * honest that the state is temporary, so reloading is worth doing.
 */
export const PAGE_ANALYSIS_UNAVAILABLE_MESSAGE =
  'We could not load the Merlin analysis for this ticket, so feedback cannot be submitted right now. Please reload the page to try again.';

export function pageMissingParameterMessage(parameterName: string): string {
  return `This feedback link is missing the "${parameterName}" parameter, so it cannot be opened.`;
}

/**
 * The loading state must name the ticket and say what is happening, not just spin.
 *
 * The wait is bounded by `CONTEXT_TIMEOUT_MS`, which is a minute. At a few seconds a bare spinner is
 * fine; at a minute it reads as a broken page, and a user who abandons a page they believe is broken
 * has been blocked from giving feedback just as effectively as one shown no form at all.
 */
export function pageLoadingMessage(ticketId: string): string {
  return `Looking up the Merlin analysis for ticket ${ticketId}…`;
}

/**
 * Shown when the not-authorized predicate is switched on. It is **off** today: Open Decision (e) is
 * unresolved and the Feedback_Page sits outside the `authorizedUsers` list, so gating here would lock
 * out every recipient of a ticket link.
 */
export const PAGE_NOT_AUTHORIZED_MESSAGE =
  'You do not have access to this feedback page.';
